// src/screens/StakeDetailsScreen.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import './StakeDetailsScreen.css';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import backIcon from '../assets/backspace-icon.png';
import { getMyStakesForPost, getStakeSummaryForPost, listStakesByPost } from '../api';

function fmt(n) { return (Number(n) || 0).toFixed(2); }
function fmtTime(ms) {
  if (ms <= 0) return 'Expired';
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (x) => String(x).padStart(2, '0');
  const parts = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  parts.push(`${pad(sec)}s`);
  return parts.join(' ');
}

/**
 * Usage:
 * <StakeDetailsScreen postId="..." currentUser="..." effectiveNetwork="mainnet" onBack={() => ...} />
 * or route-driven: /stake/:postId  (reads :postId via useParams)
 * Optionally pass currentUser via location.state or props.
 */
export default function StakeDetailsScreen({
  postId: postIdProp,
  currentUser: currentUserProp,
  effectiveNetwork = 'mainnet',
  onBack,
}) {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  const postId =
    postIdProp ||
    params?.postId ||
    location?.state?.postId ||
    '';

  const currentUser =
    currentUserProp ||
    location?.state?.currentUser ||
    '';

  const [loading, setLoading] = useState(false);

  // viewer-specific (used for header totals only)
  const [items, setItems] = useState([]);
  const [myTotals, setMyTotals] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // post-wide
  const [postTotals, setPostTotals] = useState(null);
  const [globalActive, setGlobalActive] = useState([]);

  const [serverNow, setServerNow] = useState(() => new Date());
  const listRef = useRef(null);

  useEffect(() => {
    if (!postId) return;
    setItems([]);
    setMyTotals(null);
    setPostTotals(null);
    setGlobalActive([]);
    setPage(1);
    setHasMore(true);
    prime(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId, currentUser, effectiveNetwork]);

  async function prime(firstPage) {
    try {
      setLoading(true);

      const [{ data: mine }, { data: postSum }, { data: global }] = await Promise.all([
        // your ACTIVE totals (auth)
        getMyStakesForPost(postId, {
          page: firstPage,
          limit: 20,
          network: effectiveNetwork,
        }).catch(() => ({
          data: { items: [], totals: null, hasMore: false, now: new Date().toISOString() }
        })),
        // totals (public)
        getStakeSummaryForPost(postId, {
          viewer: currentUser,
          network: effectiveNetwork,
        }),
        // all stakes (public)
        listStakesByPost(postId, { page: 1, limit: 50, network: effectiveNetwork }),
      ]);

      // viewer totals for header
      const arr = Array.isArray(mine?.items) ? mine.items : [];
      setItems(arr);
      setMyTotals(mine?.totals || null);
      setHasMore(!!mine?.hasMore);
      setPage(firstPage);
      setServerNow(mine?.now ? new Date(mine.now) : new Date());

      // post totals for header
      setPostTotals({
        activeTotalPi: Number(
          postSum?.stakeActivePi ??
          postSum?.totalActiveRefundable ??
          0
        ),
        activeCount: Array.isArray(postSum?.recentStakers) ? postSum.recentStakers.length : 0,
        nextExpiryAt: postSum?.stakeNextExpiryAt || null,
      });

      // global active (soonest expiry first)
      const now = new Date();
      const gItems = Array.isArray(global?.items) ? global.items : [];
      const filtered = gItems
        .filter(s =>
          s?.status === 'ACTIVE' &&
          s?.expiresAt && new Date(s.expiresAt) > now &&
          (s?.network || effectiveNetwork) === effectiveNetwork
        )
        .sort((a, b) => new Date(a.expiresAt) - new Date(b.expiresAt))
        .map(s => ({
          _id: s._id,
          amountPi: Number(s.amountPi || 0),
          refundablePi: Number(s.refundablePi || 0),
          expiresAt: s.expiresAt,
          status: s.status,
        }));
      setGlobalActive(filtered);
    } catch (e) {
      console.error('❌ Failed to load stake details/summary:', e);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }

  const fetchPage = async (p) => {
    try {
      setLoading(true);
      const { data } = await getMyStakesForPost(postId, {
        page: p,
        limit: 20,
        network: effectiveNetwork,
      });
      const arr = Array.isArray(data?.items) ? data.items : [];
      setItems(prev => [...prev, ...arr]);
      setMyTotals(data?.totals || myTotals);
      setHasMore(!!data?.hasMore);
      setPage(p);
      setServerNow(data?.now ? new Date(data.now) : serverNow);
    } catch (e) {
      console.error('❌ Failed to load more stakes:', e);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  // live countdown (1s tick)
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const now = useMemo(() => new Date(), [tick]);

  // Derived header numbers (ACTIVE only)
  const yourTotalPi = fmt(
    (Array.isArray(items) ? items : [])
      .reduce((sum, s) => {
        const active = s?.status === 'ACTIVE' && new Date(s?.expiresAt) > new Date();
        return active ? sum + Number(s?.amountPi || 0) : sum;
      }, 0)
  );
  const activeTotalPiAll = fmt(postTotals?.activeTotalPi ?? 0);

  const handleBack = () => {
    if (typeof onBack === 'function') return onBack();
    navigate(-1);
  };

  return (
    <div className="stake-screen">
      {/* Fixed top bar */}
      <div className="stake-topbar">
        <button className="back-btn" onClick={handleBack} aria-label="Back">
          <img src={backIcon} alt="Back" />
        </button>
        <div className="topbar-title">Stake Detail</div>
        <div className="topbar-spacer" />
      </div>

      {/* Scrollable content area */}
      <div className="stake-content" ref={listRef}>
        {/* Header summary */}
        <div className="summary-grid">
          <div className="summary-card">
            <span className="label">Your stakes</span>
            <span className="value">{yourTotalPi} Pi</span>
          </div>
          <div className="summary-card">
            <span className="label">Actively staked</span>
            <span className="value">{activeTotalPiAll} Pi</span>
          </div>
        </div>

        {/* Active global stakes list */}
        <div className="section-title">Active stakes on this post</div>

        {globalActive.length === 0 && loading && (
          <div className="center"><div className="spinner" /></div>
        )}

        {globalActive.length === 0 && !loading && (
          <div className="empty">No active stakes yet.</div>
        )}

        {globalActive.length > 0 && (
          <div className="stake-list">
            {globalActive.map((s) => {
              const exp = new Date(s.expiresAt);
              const remaining = exp.getTime() - now.getTime();
              const isActive = s.status === 'ACTIVE' && remaining > 0;
              return (
                <div key={s._id} className="stake-row">
                  <div className="stake-row-main">
                    <div className="stake-amount">
                      <span className="row-label">Amount</span>
                      <span className="row-value">{fmt(s.amountPi)} Pi</span>
                    </div>
                    <div className="stake-status">
                      <span className={`pill ${isActive ? 'active' : 'expired'}`}>
                        {isActive ? 'Active' : 'Expired'}
                      </span>
                    </div>
                  </div>
                  <div className="stake-row-sub">
                    <div>
                      <span className="row-label">Refundable</span>
                      <span className="row-value">{fmt(s.refundablePi)} Pi</span>
                    </div>
                    <div>
                      <span className="row-label">Expires</span>
                      <span className="row-value countdown">
                        {isActive ? `in ${fmtTime(remaining)}` : exp.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Load more for "your active stakes" (if needed later) */}
        {hasMore && (
          <button className="load-more" disabled={loading} onClick={() => fetchPage(page + 1)}>
            {loading ? 'Loading…' : 'Load more'}
          </button>
        )}
      </div>
    </div>
  );
}
